/**
 * Utility functions for detecting changes between objects for PATCH requests
 */

/**
 * Detects changes between two objects and returns only the changed fields
 * @param original The original object
 * @param updated The updated object
 * @returns Object containing only the changed fields
 */
export const detectChanges = <T extends Record<string, any>>(
  original: T,
  updated: T
): Partial<T> => {
  const changes: Partial<T> = {};

  for (const key in updated) {
    if (updated.hasOwnProperty(key)) {
      const originalValue = original[key];
      const updatedValue = updated[key];

      // Handle different data types
      if (originalValue !== updatedValue) {
        // Special handling for dates
        if (originalValue && updatedValue && 
            typeof originalValue === 'object' && typeof updatedValue === 'object' &&
            originalValue.constructor === Date && updatedValue.constructor === Date) {
          if ((originalValue as Date).getTime() !== (updatedValue as Date).getTime()) {
            changes[key] = updatedValue;
          }
        }
        // Special handling for arrays
        else if (Array.isArray(originalValue) && Array.isArray(updatedValue)) {
          if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
            changes[key] = updatedValue;
          }
        }
        // Regular comparison for primitives and objects
        else {
          changes[key] = updatedValue;
        }
      }
    }
  }

  return changes;
};

/**
 * Detects changes between form data and existing entity for PATCH requests
 * @param original The original entity
 * @param formData The form data
 * @returns Object containing only the changed fields
 */
export const detectFormChanges = <T extends Record<string, any>>(
  original: T,
  formData: T
): Partial<T> => {
  return detectChanges(original, formData);
};

/**
 * Checks if there are any changes between two objects
 * @param original The original object
 * @param updated The updated object
 * @returns true if there are changes, false otherwise
 */
export const hasChanges = <T extends Record<string, any>>(
  original: T,
  updated: T
): boolean => {
  const changes = detectChanges(original, updated);
  return Object.keys(changes).length > 0;
};
